import { FC, memo, useCallback, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import { Document, Page } from 'react-pdf'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'
import IconButton from '@mui/material/IconButton'
import { OnDocumentLoadSuccess } from 'react-pdf/dist/cjs/shared/types'
import Typography from '@mui/material/Typography'
import CircularProgress from '../CircularProgress'

const PdfPreviewPanel: FC<{
  url?: string | null
  loading?: boolean
  renderTextLayer?: boolean
  renderAnnotationLayer?: boolean
}> = ({ url, loading, renderTextLayer = true, renderAnnotationLayer = true }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState<number | undefined>(1)
  const zoomIn = useCallback(() => {
    setScale((prevScale = 1) => {
      return Math.max(Math.min(prevScale + 0.1, 2), 0.2)
    })
  }, [setScale])
  const zoomOut = useCallback(() => {
    setScale((prevScale = 1) => {
      return Math.max(Math.min(prevScale - 0.1, 2), 0.2)
    })
  }, [setScale])
  const handleWheel: React.WheelEventHandler<HTMLDivElement> = useCallback(
    event => {
      if (event.ctrlKey) {
        setScale((prevScale = 1) => {
          const newScale = event.deltaY < 0 ? prevScale + 0.1 : prevScale - 0.1
          return Math.max(Math.min(newScale, 2), 0.5) // Prevent zooming out too far
        })
        event.preventDefault()
        event.stopPropagation()
      }
    },
    [setScale],
  )
  const [numPages, setNumPages] = useState(0)

  const onDocumentLoadSuccess: OnDocumentLoadSuccess = useCallback(
    pdf => {
      pdf.getPage(1).then(page => {
        const viewport = page.getViewport({ scale: 1 })
        const newScale =
          Math.min((containerRef.current?.clientWidth || window.innerWidth) - 36, 842) /
          viewport.width
        setScale(newScale)
        setNumPages(pdf.numPages)
      })
    },
    [setScale],
  )

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        display: 'flex',
        flex: 1,
        overflow: 'hidden',
        backgroundColor: 'pdfBackground',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          flex: 1,
          overflow: 'scroll',
          backgroundColor: 'pdfBackground',
          paddingTop: 6,
        }}
      >
        <Box
          component={'div'}
          onWheel={handleWheel}
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'pdfBackground',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          {url && (
            <Document file={url} onLoadSuccess={onDocumentLoadSuccess}>
              {Array.from({ length: numPages }, (_, index) => (
                <Box key={`page_${index + 1}`} sx={{ marginBottom: 6 }}>
                  <Page
                    pageNumber={index + 1}
                    scale={scale}
                    renderTextLayer={renderTextLayer}
                    renderAnnotationLayer={renderAnnotationLayer}
                  />
                </Box>
              ))}
            </Document>
          )}
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              <CircularProgress delay={200} />
            </Box>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          position: 'absolute',
          top: '0',
          left: '50%',
          transform: 'translate(-50%, 0)',
          zIndex: 2,
          paddingTop: 0.5,
          paddingBottom: 0.5,
        }}
      >
        <IconButton
          aria-label="zoom out"
          color="primary"
          sx={{
            backgroundColor: 'pdfBackground',
            borderRadius: '50%',
            ':hover': {
              backgroundColor: 'pdfBackground',
            },
            ':hover:focus': {
              backgroundColor: 'pdfBackground',
            },
          }}
          onClick={zoomOut}
          disableRipple
        >
          <ZoomOutIcon sx={{ color: 'black' }} />
        </IconButton>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'default',
            backgroundColor: 'pdfBackground',
            width: '80px',
            marginLeft: '-20px',
            marginRight: '-20px',
          }}
        >
          <Typography variant="button">{((scale || 1) * 100).toFixed(0)}%</Typography>
        </Box>
        <IconButton
          aria-label="zoom in"
          color="primary"
          sx={{
            transform: 'scaleX(-1)',
            borderRadius: '50%',
            backgroundColor: 'pdfBackground',
            ':hover': {
              backgroundColor: 'pdfBackground',
            },
            ':hover:focus': {
              backgroundColor: 'pdfBackground',
            },
          }}
          onClick={zoomIn}
          disableRipple
        >
          <ZoomInIcon sx={{ color: 'black' }} />
        </IconButton>
      </Box>
    </Box>
  )
}
export default memo(PdfPreviewPanel)
